import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useRef, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import useOnClickOutside from 'use-onclickoutside'
import isURL from 'validator/lib/isURL'
import { z } from 'zod'

import Button from 'components/Button'
import StatusBanner from 'components/StatusBanner'
import Input from 'components/fields/Input'
import { testRoutingRules } from 'lib/api'
import { ensureProtocol } from 'lib/helpers'
import { Rule } from 'types/rules'
import { Template } from 'types/template'

const PopoverContainer = styled.div`
	background-color: ${({ theme }) => theme.colors.light.primaryBackgroundColor};
	border-radius: ${({ theme }) => theme.borderRadiuses.xl};
	box-shadow: ${({ theme }) => theme.boxShadows.md};
	right: 40px;
	padding: ${({ theme }) => theme.spaces.space5};
	position: fixed;
	top: 95px;
	width: 320px;
	z-index: ${({ theme }) => theme.zIndexes.popover};
`

const ContentWrapper = styled.div`
	display: flex;
	flex-flow: column;
	gap: ${({ theme }) => theme.spaces.space2};
	padding: 0px;
	width: 100%;
`

const labelStyles = css`
	font-size: 0.875rem;
	font-weight: 400;
`

const InputWrapper = styled.div`
	display: flex;
	align-items: flex-end;
	gap: ${({ theme }) => theme.spaces.space3};
	width: 100%;
`

const WebsiteInput = styled(Input)`
	${({ theme }) => theme.typography.caption};
	margin: 0;
`

const StyledButton = styled(Button)`
	padding: 12px;
	bottom: 13px;
`

const schema = z.object({
	url: z
		.string()
		.min(1, 'URL is required')
		.superRefine((value, ctx) => {
			if (!isURL(value)) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'Invalid URL format',
				})
				return z.NEVER
			}

			if (value.includes('@')) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'Please provide real address',
				})
				return z.NEVER
			}
		}),
})

type FormValues = z.infer<typeof schema>

const TestRulePopover = ({
	onClose,
	onHighlightRule,
	templates,
	localRules,
}: {
	onClose: () => void
	onHighlightRule: (id: string | null) => void
	templates: Template[]
	localRules: Rule[]
}) => {
	const [status, setStatus] = useState<'success' | 'failed' | 'checking' | 'no-match' | null>(null)
	const [ruleInfo, setRuleInfo] = useState<{ templateName: string; ruleName: string } | null>(null)
	const popoverRef = useRef<HTMLDivElement>(null)

	const formMethods = useForm<FormValues>({
		mode: 'onChange',
		resolver: zodResolver(schema),
	})

	const {
		handleSubmit,
		register,
		formState: { errors, isValid },
	} = formMethods

	const handleTest = useCallback(
		async (data: FormValues) => {
			setStatus('checking')

			const formattedUrl = ensureProtocol(data.url)

			try {
				const matchingRule = await testRoutingRules(localRules, formattedUrl)
				if (matchingRule) {
					const template = templates.find((t) => t.id === matchingRule.templateId)
					setRuleInfo({
						templateName: template?.name || '',
						ruleName: matchingRule.name || '',
					})
					setStatus('success')
					onHighlightRule(matchingRule.id)
				} else {
					setStatus('no-match')
					onHighlightRule(null)
				}
			} catch {
				setStatus('failed')
				onHighlightRule(null)
			}
		},
		[localRules, onHighlightRule, templates],
	)

	useOnClickOutside(popoverRef, () => {
		onHighlightRule(null)
		onClose()
	})

	return (
		<PopoverContainer ref={popoverRef} data-testid="test-rule-popover">
			<FormProvider {...formMethods}>
				<form onSubmit={handleSubmit(handleTest)}>
					<ContentWrapper>
						<InputWrapper>
							<WebsiteInput
								type="text"
								{...register('url')}
								label="Check which rule is triggered for the given URL:"
								labelCss={labelStyles}
								placeholder="Enter URL"
								error={errors.url?.message}
							/>
							<StyledButton
								kind="primary"
								type="submit"
								loading={status === 'checking'}
								disabled={status === 'checking' || !isValid}
								disableHover
							>
								Test
							</StyledButton>
						</InputWrapper>
						{status && (
							<>
								{status === 'success' && (
									<StatusBanner type="success" isVisible={true}>
										Provided website is linked to the <strong>{ruleInfo?.templateName}</strong> template widget because
										of the <strong>{ruleInfo?.ruleName}</strong> rule.
									</StatusBanner>
								)}
								{status === 'no-match' && (
									<StatusBanner type="warning" isVisible={true}>
										There are no rules matching the provided URL.
									</StatusBanner>
								)}
								{status === 'failed' && (
									<StatusBanner type="warning" title="Something went wrong" isVisible={true}>
										Looks like a network problem. Please try again or test the rules later.
									</StatusBanner>
								)}
							</>
						)}
					</ContentWrapper>
				</form>
			</FormProvider>
		</PopoverContainer>
	)
}

export default TestRulePopover
